// matches libs/styles/_root-variable-colors

export const VariableColor = {
  Primary100: 'primary100',
  Primary200: 'primary200',
  Primary300: 'primary300',
  Primary400: 'primary400',
  Primary500: 'primary500',
  Primary600: 'primary600',
  Primary700: 'primary700',
  Primary800: 'primary800',
  Primary900: 'primary900',
  Primary1000: 'primary1000',
  Primary1100: 'primary1100',
  Primary1200: 'primary1200',

  Gray100: 'gray100',
  Gray200: 'gray200',
  Gray300: 'gray300',
  Gray400: 'gray400',
  Gray500: 'gray500',
  Gray600: 'gray600',
  Gray700: 'gray700',
  Gray800: 'gray800',
  Gray900: 'gray900',
  Gray1000: 'gray1000',
  Gray1100: 'gray1100',
  Gray1200: 'gray1200',

  Red100: 'red100',
  Red200: 'red200',
  Red300: 'red300',
  Red400: 'red400',
  Red500: 'red500',
  Red600: 'red600',
  Red700: 'red700',
  Red800: 'red800',
  Red900: 'red900',
  Red1000: 'red1000',
  Red1100: 'red1100',
  Red1200: 'red1200',

  Orange100: 'orange100',
  Orange200: 'orange200',
  Orange300: 'orange300',
  Orange400: 'orange400',
  Orange500: 'orange500',
  Orange600: 'orange600',
  Orange700: 'orange700',
  Orange800: 'orange800',
  Orange900: 'orange900',
  Orange1000: 'orange1000',
  Orange1100: 'orange1100',
  Orange1200: 'orange1200',

  White: 'white',
  Black: 'black',
  Purple: 'purple',

  AssetHealthHealthy: 'healthy',
  AssetHealthAtRisk: 'at-risk',
  AssetHealthUnhealthy: 'unhealthy',
  AssetHealthInfo: 'info',
} as const;

export type VariableColor = (typeof VariableColor)[keyof typeof VariableColor];

export const colors = {
  primary100: '#fbf8ff',
  primary200: '#edebfe',
  primary300: '#d2cffd',
  primary400: '#b4b1fd',
  primary500: '#9a92fe',
  primary600: '#8672ff',
  primary700: '#7f4eff',
  primary800: '#742af5',
  primary900: '#6120d1',
  primary1000: '#4d20a7',
  primary1100: '#361d73',
  primary1200: '#311b68',
  primary100Dark: '#201542',
  primary200Dark: '#2b1a5b',
  primary300Dark: '#401e8a',
  primary400Dark: '#5620bc',
  primary500Dark: '#6e26ec',
  primary600Dark: '#7e47ff',
  primary700Dark: '#836dff',
  primary800Dark: '#948afd',
  primary900Dark: '#a8a4fd',
  primary1000Dark: '#c0bdfc',
  primary1100Dark: '#dddcfd',
  primary1200Dark: '#e4e2fd',

  gray100: '#fbf9fb',
  gray200: '#edecef',
  gray300: '#d4d3d8',
  gray400: '#b7b8bf',
  gray500: '#9d9ea7',
  gray600: '#858791',
  gray700: '#70737e',
  gray800: '#5e606b',
  gray900: '#4d505b',
  gray1000: '#3f414c',
  gray1100: '#2d2f37',
  gray1200: '#292b33',
  gray100Dark: '#1e2025',
  gray200Dark: '#26282e',
  gray300Dark: '#373943',
  gray400Dark: '#484b55',
  gray500Dark: '#5b5e69',
  gray600Dark: '#6f727d',
  gray700Dark: '#838690',
  gray800Dark: '#999aa3',
  gray900Dark: '#afafb7',
  gray1000Dark: '#c5c5cc',
  gray1100Dark: '#dddce1',
  gray1200Dark: '#e9e7eb',

  red100: '#fff8f7',
  red200: '#ffe8e5',
  red300: '#ffc5be',
  red400: '#fda098',
  red500: '#f37b74',
  red600: '#e45957',
  red700: '#cf3e43',
  red800: '#b32f39',
  red900: '#942a34',
  red1000: '#76252e',
  red1100: '#76252e',
  red1200: '#4d1b20',
  red100Dark: '#361618',
  red200Dark: '#461a1e',
  red300Dark: '#672229',
  red400Dark: '#882932',
  red500Dark: '#ae2e37',
  red600Dark: '#ce3d42',
  red700Dark: '#e35755',
  red800Dark: '#f17670',
  red900Dark: '#fa948c',
  red1000Dark: '#ffb3ab',
  red1100Dark: '#ffd3ce',
  red1200Dark: '#ffe1de',

  orange100: '#fffaf7',
  orange200: '#ffe9dd',
  orange300: '#ffc8a8',
  orange400: '#ffa270',
  orange500: '#ec8348',
  orange600: '#ce6d33',
  orange700: '#af5d2a',
  orange800: '#915026',
  orange900: '#784421',
  orange1000: '#61371b',
  orange1100: '#452915',
  orange1200: '#3f2514',
  orange100Dark: '#2e1c10',
  orange200Dark: '#3a2313',
  orange300Dark: '#553119',
  orange400Dark: '#6f3f1f',
  orange500Dark: '#8d4e25',
  orange600Dark: '#ad5d2a',
  orange700Dark: '#cc6c32',
  orange800Dark: '#e87f44',
  orange900Dark: '#fa9761',
  orange1000Dark: '#ffb58c',
  orange1100Dark: '#ffd4bb',
  orange1200Dark: '#ffe2d1',

  black: '#17181c',
  white: '#ffffff',
  purple: '#f4f4fb',
  purpleDark: '#000000cc',

  //Asset health severities
  healthyLight: '#1f8353',
  healthyDark: '#206b43',
  warningLight: '#f6cf5a',
  warningDark: '#e9c24a',
  errorLight: '#cf3e43',
  errorDark: '#ce3d42',
  infoLight: '#9d9ea7',
  infoDark: '#5b5e69',
};

export const VARIABLE_COLORS: Record<VariableColor, { light: string; dark: string }> = {
  [VariableColor.Primary100]: { light: colors.primary100, dark: colors.primary100Dark },
  [VariableColor.Primary200]: { light: colors.primary200, dark: colors.primary200Dark },
  [VariableColor.Primary300]: { light: colors.primary300, dark: colors.primary300Dark },
  [VariableColor.Primary400]: { light: colors.primary400, dark: colors.primary400Dark },
  [VariableColor.Primary500]: { light: colors.primary500, dark: colors.primary500Dark },
  [VariableColor.Primary600]: { light: colors.primary600, dark: colors.primary600Dark },
  [VariableColor.Primary700]: { light: colors.primary700, dark: colors.primary700Dark },
  [VariableColor.Primary800]: { light: colors.primary800, dark: colors.primary800Dark },
  [VariableColor.Primary900]: { light: colors.primary900, dark: colors.primary900Dark },
  [VariableColor.Primary1000]: { light: colors.primary1000, dark: colors.primary1000Dark },
  [VariableColor.Primary1100]: { light: colors.primary1100, dark: colors.primary1100Dark },
  [VariableColor.Primary1200]: { light: colors.primary1200, dark: colors.primary1200Dark },

  [VariableColor.Gray100]: { light: colors.gray100, dark: colors.gray100Dark },
  [VariableColor.Gray200]: { light: colors.gray200, dark: colors.gray200Dark },
  [VariableColor.Gray300]: { light: colors.gray300, dark: colors.gray300Dark },
  [VariableColor.Gray400]: { light: colors.gray400, dark: colors.gray400Dark },
  [VariableColor.Gray500]: { light: colors.gray500, dark: colors.gray500Dark },
  [VariableColor.Gray600]: { light: colors.gray600, dark: colors.gray600Dark },
  [VariableColor.Gray700]: { light: colors.gray700, dark: colors.gray700Dark },
  [VariableColor.Gray800]: { light: colors.gray800, dark: colors.gray800Dark },
  [VariableColor.Gray900]: { light: colors.gray900, dark: colors.gray900Dark },
  [VariableColor.Gray1000]: { light: colors.gray1000, dark: colors.gray1000Dark },
  [VariableColor.Gray1100]: { light: colors.gray1100, dark: colors.gray1100Dark },
  [VariableColor.Gray1200]: { light: colors.gray1200, dark: colors.gray1200Dark },

  [VariableColor.Red100]: { light: colors.red100, dark: colors.red100Dark },
  [VariableColor.Red200]: { light: colors.red200, dark: colors.red200Dark },
  [VariableColor.Red300]: { light: colors.red300, dark: colors.red300Dark },
  [VariableColor.Red400]: { light: colors.red400, dark: colors.red400Dark },
  [VariableColor.Red500]: { light: colors.red500, dark: colors.red500Dark },
  [VariableColor.Red600]: { light: colors.red600, dark: colors.red600Dark },
  [VariableColor.Red700]: { light: colors.red700, dark: colors.red700Dark },
  [VariableColor.Red800]: { light: colors.red800, dark: colors.red800Dark },
  [VariableColor.Red900]: { light: colors.red900, dark: colors.red900Dark },
  [VariableColor.Red1000]: { light: colors.red1000, dark: colors.red1000Dark },
  [VariableColor.Red1100]: { light: colors.red1100, dark: colors.red1100Dark },
  [VariableColor.Red1200]: { light: colors.red1200, dark: colors.red1200Dark },

  [VariableColor.Orange100]: { light: colors.orange100, dark: colors.orange100Dark },
  [VariableColor.Orange200]: { light: colors.orange200, dark: colors.orange200Dark },
  [VariableColor.Orange300]: { light: colors.orange300, dark: colors.orange300Dark },
  [VariableColor.Orange400]: { light: colors.orange400, dark: colors.orange400Dark },
  [VariableColor.Orange500]: { light: colors.orange500, dark: colors.orange500Dark },
  [VariableColor.Orange600]: { light: colors.orange600, dark: colors.orange600Dark },
  [VariableColor.Orange700]: { light: colors.orange700, dark: colors.orange700Dark },
  [VariableColor.Orange800]: { light: colors.orange800, dark: colors.orange800Dark },
  [VariableColor.Orange900]: { light: colors.orange900, dark: colors.orange900Dark },
  [VariableColor.Orange1000]: { light: colors.orange1000, dark: colors.orange1000Dark },
  [VariableColor.Orange1100]: { light: colors.orange1100, dark: colors.orange1100Dark },
  [VariableColor.Orange1200]: { light: colors.orange1200, dark: colors.orange1200Dark },

  [VariableColor.White]: { light: colors.white, dark: colors.white },
  [VariableColor.Black]: { light: colors.black, dark: colors.black },
  [VariableColor.Purple]: { light: colors.purple, dark: colors.purpleDark },

  [VariableColor.AssetHealthHealthy]: { light: colors.healthyLight, dark: colors.healthyDark },
  [VariableColor.AssetHealthAtRisk]: { light: colors.warningLight, dark: colors.warningDark },
  [VariableColor.AssetHealthUnhealthy]: { light: colors.errorLight, dark: colors.errorDark },
  [VariableColor.AssetHealthInfo]: { light: colors.infoLight, dark: colors.infoDark },
};

export const getVariableColor = (color: VariableColor, isDarkMode = false): string => {
  return isDarkMode ? VARIABLE_COLORS[color].dark : VARIABLE_COLORS[color].light;
};
